/**
 * MONSTER TOWN — VENUE INTERIORS. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * Registers the interior scenes a kid walks into and builds their shell. One
 * generic room builder serves every venue and the town shop, driven entirely by
 * `VenueShell` data — venue #2's room is a colour table, not new code.
 *
 * DOLL-HOUSE WALLS. All four walls exist, but the two nearest the camera are
 * hidden every frame based on the iso camera's 90-degree snap, so the kid is
 * never looking at the back of a wall and the room always reads. This is the
 * only reason a fixed-rotation camera can afford real walls at all.
 */

import * as THREE from 'three'
import { hex } from '../core/palette'
import { world, type SceneNode } from '../core/scene'
import type { GridSpec } from '../core/grid'
import type { SceneId } from '../core/types'
import { mix } from '../core/palette'
import { boxGeo, boxOn, cone, cylinder, generatedTexture, group, planeGeo, toon } from '../render/kit'
import type { IsoCamera } from '../render/camera'
import type { VenueFloorStyle, VenueShell } from './types'

export interface RoomSpec {
  readonly sceneId: SceneId
  readonly devName: string
  readonly grid: GridSpec
  readonly shell: VenueShell
}

interface RoomWalls {
  readonly sceneId: SceneId
  readonly N: THREE.Group
  readonly S: THREE.Group
  readonly E: THREE.Group
  readonly W: THREE.Group
}

const ROOMS: RoomWalls[] = []

/** Which two walls sit between the camera and the room, per rotation snap. */
const HIDE: readonly (readonly ('N' | 'S' | 'E' | 'W')[])[] = [
  ['S', 'E'],
  ['N', 'E'],
  ['N', 'W'],
  ['S', 'W'],
]

/**
 * FLOORS ARE WHAT MAKES TWO ROOMS DIFFERENT ROOMS.
 *
 * Every venue used to get the same checkerboard, so the restaurant was the ice
 * cream shop in tan and the town shop was the ice cream shop in cream. The
 * pattern is now shell DATA (`VenueShell.floorStyle`) and venue #3 picks one
 * without touching this builder.
 *
 * All three are code-generated at 128px and cached by key — zero external
 * assets, and a repeat of one texture per room however big the floor is.
 */
function floorTexture(style: VenueFloorStyle, a: string, b: string): THREE.Texture {
  return generatedTexture(`venueFloor:${style}:${a}:${b}`, (c, s) => {
    const h = s / 2
    c.fillStyle = a
    c.fillRect(0, 0, s, s)

    if (style === 'planks') {
      // Long boards with staggered ends and a nail at each joint. Under a fixed
      // iso camera a plank floor's grain runs at 45 degrees to a chequer's, so
      // the two never read as the same surface even in the same colours.
      c.fillStyle = b
      for (let row = 0; row < 4; row++) {
        if (row % 2 === 0) continue
        c.fillRect(0, (row * s) / 4, s, s / 4)
      }
      c.strokeStyle = 'rgba(0,0,0,0.16)'
      c.lineWidth = 3
      for (let row = 0; row <= 4; row++) {
        c.beginPath()
        c.moveTo(0, (row * s) / 4)
        c.lineTo(s, (row * s) / 4)
        c.stroke()
      }
      // board ends, staggered so it is not a grid
      for (let row = 0; row < 4; row++) {
        const x = ((row % 2 === 0 ? 1 : 3) * s) / 4
        c.beginPath()
        c.moveTo(x, (row * s) / 4)
        c.lineTo(x, ((row + 1) * s) / 4)
        c.stroke()
      }
      c.fillStyle = 'rgba(0,0,0,0.18)'
      for (let row = 0; row < 4; row++) {
        const x = ((row % 2 === 0 ? 1 : 3) * s) / 4
        c.beginPath()
        c.arc(x + 7, (row * s) / 4 + s / 8, 2.5, 0, Math.PI * 2)
        c.fill()
      }
      return
    }

    if (style === 'diamond') {
      // A harlequin diamond: same two colours, rotated 45 degrees, with a small
      // dot at each meeting point. Reads as a shop floor rather than a kitchen.
      c.fillStyle = b
      c.beginPath()
      c.moveTo(h, 0)
      c.lineTo(s, h)
      c.lineTo(h, s)
      c.lineTo(0, h)
      c.closePath()
      c.fill()
      c.strokeStyle = 'rgba(0,0,0,0.08)'
      c.lineWidth = 2
      c.stroke()
      c.fillStyle = 'rgba(0,0,0,0.14)'
      for (const [dx, dy] of [
        [0, 0],
        [s, 0],
        [0, s],
        [s, s],
        [h, h],
      ] as [number, number][]) {
        c.beginPath()
        c.arc(dx, dy, 4, 0, Math.PI * 2)
        c.fill()
      }
      return
    }

    c.fillStyle = b
    c.fillRect(0, 0, h, h)
    c.fillRect(h, h, h, h)
    // soft grout so the tiles read as tiles at any zoom
    c.strokeStyle = 'rgba(0,0,0,0.06)'
    c.lineWidth = 2
    c.strokeRect(0, 0, h, h)
    c.strokeRect(h, h, h, h)
  })
}

/**
 * ===========================================================================
 *  A WINDOW WITH NOTHING IN IT IS A MISSING POSTER (fixed vr2 — BLOCKER half)
 * ===========================================================================
 * Critic, on `08-inside-the-zoo.png`: *"three wall panels are empty grey-green
 * rectangles on brown ledges that read as missing posters."* They are the
 * windows, and they were `glassy('skyHorizon', 0.9)` — one flat pale fill,
 * quantised by a toon shader into a single band, on a pale blue wall. There was
 * genuinely nothing in them.
 *
 * There is now a VIEW: sky, a hill, a cloud, a tree line, and the light coming
 * in over the sill. It is one generated 256px canvas and ONE material shared by
 * every window in every venue and the town shop — so seven windows still cost
 * seven draw calls, exactly what an empty pane cost, and no new asset exists.
 *
 * Unlit (`MeshBasicMaterial`) on purpose: outside is brighter than in, so a
 * window has to keep its value when the room is shaded and after dark, the same
 * reasoning the exit gateway's daylight panel already uses.
 */
let windowMat: THREE.Material | null = null
function windowViewMaterial(): THREE.Material {
  if (windowMat !== null) return windowMat
  const tex = generatedTexture('venueWindowView', (c, s) => {
    const css = (n: number): string => `#${n.toString(16).padStart(6, '0')}`
    const sky = c.createLinearGradient(0, 0, 0, s)
    sky.addColorStop(0, css(mix('skyTop', 'skyHorizon', 0.25)))
    sky.addColorStop(0.62, css(hex('skyHorizon')))
    sky.addColorStop(0.63, css(mix('grassLight', 'grass', 0.4)))
    sky.addColorStop(1, css(hex('grass')))
    c.fillStyle = sky
    c.fillRect(0, 0, s, s)
    // a far hill, so the horizon is a place and not a line
    c.fillStyle = css(mix('grass', 'grassDark', 0.45))
    c.beginPath()
    c.ellipse(s * 0.3, s * 0.66, s * 0.36, s * 0.16, 0, 0, Math.PI * 2)
    c.fill()
    c.beginPath()
    c.ellipse(s * 0.82, s * 0.68, s * 0.3, s * 0.12, 0, 0, Math.PI * 2)
    c.fill()
    // two trees on the skyline
    for (const [x, r] of [
      [0.2, 0.085],
      [0.68, 0.07],
    ] as [number, number][]) {
      c.fillStyle = css(hex('trunk'))
      c.fillRect(s * x - s * 0.014, s * 0.56, s * 0.028, s * 0.1)
      c.fillStyle = css(hex('leaf'))
      c.beginPath()
      c.arc(s * x, s * 0.53, s * r, 0, Math.PI * 2)
      c.fill()
    }
    // one cloud
    c.fillStyle = 'rgba(255,255,255,0.92)'
    for (const [x, y, r] of [
      [0.62, 0.2, 0.075],
      [0.72, 0.2, 0.055],
      [0.54, 0.22, 0.05],
    ] as [number, number, number][]) {
      c.beginPath()
      c.arc(s * x, s * y, s * r, 0, Math.PI * 2)
      c.fill()
    }
    // the light coming in, brightest at the bottom of the opening
    const glow = c.createLinearGradient(0, s * 0.5, 0, s)
    glow.addColorStop(0, 'rgba(255,243,214,0)')
    glow.addColorStop(1, 'rgba(255,243,214,0.42)')
    c.fillStyle = glow
    c.fillRect(0, 0, s, s)
  })
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  windowMat = new THREE.MeshBasicMaterial({ map: tex })
  return windowMat
}

/** The room: checker floor, four walls, skirting, beams, windows, lamps. */
export function buildRoomShell(node: SceneNode, shell: VenueShell): void {
  const g = node.grid
  const t = g.tileSize
  const w = g.spec.width * t
  const d = g.spec.depth * t
  const wallH = g.levelHeight * 2.2
  const cx = g.originX + w / 2
  const cz = g.originZ + d / 2

  // -- floor -------------------------------------------------------------
  const style = shell.floorStyle ?? 'checker'
  const tex = floorTexture(
    style,
    `#${hex(shell.floor).toString(16).padStart(6, '0')}`,
    `#${hex(shell.floorAlt).toString(16).padStart(6, '0')}`,
  )
  // Planks tile along their length, so they repeat half as often across and get
  // long boards instead of short blocks.
  if (style === 'planks') tex.repeat.set(g.spec.width / 4, g.spec.depth / 2)
  else tex.repeat.set(g.spec.width / 2, g.spec.depth / 2)
  const floorMat = new THREE.MeshToonMaterial({ color: 0xffffff, map: tex })
  const floor = new THREE.Mesh(planeGeo(w, d), floorMat)
  floor.position.set(cx, 0.01, cz)
  floor.receiveShadow = true
  floor.name = 'venueFloor'
  node.ground.add(floor)

  // A PLINTH, not a pad. A flat mat one hair below the floor reads as a floor
  // slab hovering in space; a solid block with a lit lip reads as a dollhouse
  // room standing on something. Matches the house rooms exactly, and gives the
  // interior ambience's contact shadow an object to sit under.
  const plinth = boxOn(w + t * 0.9, 0.34, d + t * 0.9, {
    color: mix('woodLight', 'woodDark', 0.3),
    x: cx,
    y: -0.36,
    z: cz,
    name: 'venuePlinth',
  })
  plinth.castShadow = false
  node.ground.add(plinth)
  const lip = boxOn(w + t * 1.0, 0.07, d + t * 1.0, {
    color: mix('woodLight', 'sunWarm', 0.4),
    x: cx,
    y: -0.09,
    z: cz,
    name: 'venuePlinthLip',
  })
  lip.castShadow = false
  node.ground.add(lip)

  // -- walls --------------------------------------------------------------
  const mkWall = (
    name: string,
    px: number,
    pz: number,
    sx: number,
    sz: number,
    windows: number,
    horizontal: boolean,
  ): THREE.Group => {
    const grp = group(name)
    // `sx`/`sz` are the wall box's real dimensions: one of them is the LENGTH
    // of the wall, the other is its THICKNESS. Only ever fatten the thickness
    // axis — fattening the length axis is how you get teal girders sticking
    // out past the corners of the room and glass slabs lying across the floor.
    const along = horizontal ? sx : sz
    const trimX = horizontal ? sx : sx * 1.3
    const trimZ = horizontal ? sz * 1.3 : sz

    grp.add(boxOn(sx, wallH, sz, { color: shell.wall, x: px, z: pz }))
    // Cornice and skirting: two proud bands, so a wall has a top and a bottom
    // instead of being one flat fill.
    grp.add(boxOn(trimX, t * 0.22, trimZ, { color: shell.wallTrim, x: px, y: wallH, z: pz }))
    grp.add(boxOn(trimX, t * 0.2, trimZ, { color: shell.wallTrim, x: px, z: pz }))

    /**
     * WINDOWS AND LAMPS SHARE ONE RHYTHM.
     *
     * They used to be laid out by two unrelated rules — windows at
     * (i+1)/(n+1), sconces at a hard-coded +/-0.3 — at three different heights,
     * and the result read as unplaced placeholder decals: four blank pale quads
     * with a gold bar under them and small coloured squares stuck at random
     * heights. Now the wall is divided into (n+1) bays: a window in the middle
     * of each interior boundary, a lamp in the middle of each bay, every one at
     * the same height on every wall of every venue.
     */
    const bays = windows + 1
    const eye = wallH * 0.6

    /**
     * DECOR IS MOUNTED ON THE INNER FACE, NOT THROUGH THE MIDDLE OF THE WALL.
     *
     * Everything on a wall used to be centred on the wall's own centre line and
     * made slightly FATTER than the wall so that a sliver poked out of each
     * side. What a kid actually saw of a lamp was that sliver: a 20-pixel
     * coloured lozenge floating on the plaster, which is exactly the "small
     * coloured squares stuck at random heights" in the critique. `inX`/`inZ`
     * push each piece to the room side of the wall, so a lamp is a whole lamp.
     */
    const inX = horizontal ? 0 : -Math.sign(px - cx)
    const inZ = horizontal ? -Math.sign(pz - cz) : 0
    const face = (depth: number): { x: number; z: number } => ({
      x: inX * (Math.abs(horizontal ? sz : sx) / 2 + depth),
      z: inZ * (Math.abs(horizontal ? sz : sx) / 2 + depth),
    })

    for (let i = 0; i < windows; i++) {
      const f = (i + 1) / bays - 0.5
      const wx = horizontal ? px + f * along : px
      const wz = horizontal ? pz : pz + f * along
      // Reveal (the recess), glass, mullion and sill, each one step further into
      // the room, so the window reads as a hole in a thick wall.
      const rv = face(0.01)
      grp.add(
        boxOn(horizontal ? t * 1.56 : t * 0.06, t * 1.3, horizontal ? t * 0.06 : t * 1.56, {
          color: shell.wallTrim,
          x: wx + rv.x,
          y: eye - t * 0.65,
          z: wz + rv.z,
        }),
      )
      const gl = face(0.05)
      const pane = new THREE.Mesh(
        boxGeo(horizontal ? t * 1.3 : t * 0.05, t * 1.04, horizontal ? t * 0.05 : t * 1.3),
        windowViewMaterial(),
      )
      pane.position.set(wx + gl.x, eye, wz + gl.z)
      pane.name = 'venueWindow'
      grp.add(pane)
      const si = face(0.13)
      grp.add(
        boxOn(horizontal ? t * 1.66 : t * 0.3, t * 0.11, horizontal ? t * 0.3 : t * 1.66, {
          color: shell.roofBeam,
          x: wx + si.x,
          y: eye - t * 0.68,
          z: wz + si.z,
        }),
      )
    }
    // Wall sconces instead of ceiling beams. Beams cross the whole room and,
    // under a fixed iso camera looking down, they sit right on top of whatever
    // the kid is doing. Lamps on the walls hide with the wall and never do.
    // One lamp per bay is right for a two-bay wall and far too many for a
    // twelve-tile shop wall — eleven sconces cost more draw calls than the
    // shelves they light. Past two bays, every other one, which still reads as
    // a rhythm because it lands on the bays either side of each window.
    for (let j = 0; j < bays; j++) {
      if (bays > 2 && j % 2 === 1) continue
      const f = (j + 0.5) / bays - 0.5
      const lx = horizontal ? px + f * along : px
      const lz = horizontal ? pz : pz + f * along
      // A wall lantern reads as one object only if the parts connect: back
      // plate ON the wall, a bracket reaching out, the shade hanging off the
      // END of the bracket with its bulb directly under it.
      const mid = face(0.14)
      const head = face(0.28)
      const lamp = group('sconce')
      lamp.position.set(lx, 0, lz)
      lamp.add(
        boxOn(horizontal ? t * 0.08 : t * 0.42, t * 0.08, horizontal ? t * 0.42 : t * 0.08, {
          color: shell.roofBeam,
          x: mid.x,
          y: eye + t * 0.56,
          z: mid.z,
        }),
      )
      const shade = cone(t * 0.26, t * 0.3, { color: shell.wallTrim, x: head.x, y: 0, z: head.z, seg: 10 })
      shade.rotation.x = Math.PI
      shade.position.y = eye + t * 0.5
      lamp.add(shade)
      const bulb = cylinder(t * 0.14, t * 0.12, { color: 'sunWarm', x: head.x, y: eye + t * 0.24, z: head.z, seg: 10 })
      bulb.material = toon('sunWarm')
      lamp.add(bulb)
      grp.add(lamp)
    }
    node.statics.add(grp)
    return grp
  }

  const th = t * 0.35
  const N = mkWall('wallN', cx, g.originZ - th / 2, w + th, th, 2, true)
  const S = mkWall('wallS', cx, g.originZ + d + th / 2, w + th, th, 1, true)
  const W = mkWall('wallW', g.originX - th / 2, cz, th, d + th, 2, false)
  const E = mkWall('wallE', g.originX + w + th / 2, cz, th, d + th, 2, false)

  /**
   * A SOFT RUG DOWN THE MIDDLE — AND IT IS AN OVAL, NOT A QUAD (venue fix, r3).
   *
   * This used to be a flat box: under the fixed iso camera a rectangle lying on
   * a chequer floor is four hard diagonal edges cutting across the tiles at an
   * angle nothing else in the room is on, which is why the round-3 critic read
   * it as "a flat orange quad... reading as a missing texture". A braided oval
   * has no straight edge to alias, it lands on the floor as an OBJECT, and it
   * is the shape every cosy rug in a picture book is.
   *
   * Colour is shell data, and `rug: null` turns it off entirely — a shop floor
   * wants its aisle clear.
   */
  const rugColor = shell.rug === undefined ? 'floorRug' : shell.rug
  if (rugColor !== null) {
    const rug = group('floorRug')
    rug.position.set(cx, 0, cz)
    const rx = w * 0.21
    const rz = d * 0.3
    const braid = cylinder(1, 0.035, { color: mix(rugColor, 'ink', 0.28), y: 0.012, seg: 28 })
    braid.scale.set(rx, 1, rz)
    rug.add(braid)
    const field = cylinder(1, 0.03, { color: rugColor, y: 0.03, seg: 28 })
    field.scale.set(rx * 0.88, 1, rz * 0.86)
    rug.add(field)
    const heart = cylinder(1, 0.026, { color: mix(rugColor, 'flowerWhite', 0.34), y: 0.042, seg: 24 })
    heart.scale.set(rx * 0.56, 1, rz * 0.52)
    rug.add(heart)
    for (const o of [braid, field, heart]) {
      o.castShadow = false
      o.receiveShadow = true
    }
    node.ground.add(rug)
  }

  ROOMS.push({ sceneId: node.def.id, N, S, E, W })
}

/** Register an interior room scene and build it. Idempotent. */
export function registerRoom(spec: RoomSpec): SceneNode {
  const existing = world.get(spec.sceneId)
  if (existing !== undefined) return existing
  const node = world.register({
    id: spec.sceneId,
    kind: 'interior',
    grid: spec.grid,
    sky: false,
    devName: spec.devName,
  })
  buildRoomShell(node, spec.shell)
  return node
}

/**
 * Hide whichever two walls are between the camera and the room. Cheap enough to
 * run every frame (four boolean writes) and it is what makes a real four-walled
 * interior work under a fixed iso camera.
 */
let lastCulledRot = -1
let roomCountAtCull = -1

export function updateWallCulling(camera: IsoCamera): void {
  const rot = camera.rotation
  // Nothing to do unless the camera actually snapped to a new quarter turn (or
  // a room was added). This runs every frame on an older iPad; it must be free.
  if (rot === lastCulledRot && ROOMS.length === roomCountAtCull) return
  lastCulledRot = rot
  roomCountAtCull = ROOMS.length
  const hidden = HIDE[rot] ?? HIDE[0] ?? []
  for (const r of ROOMS) {
    r.N.visible = !hidden.includes('N')
    r.S.visible = !hidden.includes('S')
    r.E.visible = !hidden.includes('E')
    r.W.visible = !hidden.includes('W')
  }
}

/** World-space centre of a room, for camera framing. */
export function roomCenter(sceneId: SceneId): { x: number; y: number; z: number } {
  const node = world.get(sceneId)
  if (node === undefined) return { x: 0, y: 0, z: 0 }
  const g = node.grid
  return {
    x: g.originX + (g.spec.width * g.tileSize) / 2,
    y: 0,
    z: g.originZ + (g.spec.depth * g.tileSize) / 2,
  }
}
